import React, { useState, useEffect } from "react";
import { Layers, Copy, ExternalLink, Zap } from "lucide-react";

/**
 * RelevantCardsPanel — KB case-study cards surfaced during a live meeting.
 *
 * These are the retrieval-only cards emitted by the backend `emit_card`
 * (WS type "relevant_card"): the top knowledge-base match for the current
 * moment, shown BEFORE the enriched AI suggestion streams in. Kept in their
 * own panel (collapsed by default) so they don't crowd the AI Suggestions or
 * Engineering Practices panels.
 */
function RelevantCard({ card, onCopy, isNew }) {
  const copyText = card.content || card.title || "";
  const scorePct = card.score != null ? Math.round(card.score * 100) : null;

  return (
    <div
      className={`relative rounded-lg border p-3 mb-3 transition-all ${
        isNew ? "border-sky-400 shadow-md" : "border-gray-200"
      }`}
      style={{ background: isNew ? "#f0f9ff" : "#fff" }}
    >
      {isNew && (
        <span className="absolute -top-2 -right-2 flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-600 text-white shadow">
          <Zap size={9} />
          Live
        </span>
      )}

      {/* Header: source/category badge + copy */}
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide text-sky-700 bg-sky-50">
          {card.category || "Knowledge Base"}
        </span>
        <button
          type="button"
          onClick={() => onCopy(copyText)}
          className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
          title="Copy content"
        >
          <Copy size={12} />
        </button>
      </div>

      {/* Title */}
      <div className="font-semibold text-gray-900 text-sm mb-1.5 leading-snug">
        {card.title}
      </div>

      {/* Snippet */}
      {card.content && (
        <div className="text-gray-700 text-xs leading-relaxed line-clamp-4 mb-2">
          {card.content}
        </div>
      )}

      {/* Reference link */}
      {card.url && (
        <a
          href={card.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-sky-600 hover:text-sky-800 transition-colors mb-1"
        >
          <ExternalLink size={11} />
          View case study
        </a>
      )}

      {/* Tags */}
      {card.tags && card.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {card.tags.slice(0, 6).map((tag) => (
            <span
              key={tag}
              className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200"
            >
              {tag}
            </span>
          ))}
          {card.tags.length > 6 && (
            <span className="text-[10px] text-gray-400">+{card.tags.length - 6}</span>
          )}
        </div>
      )}

      {scorePct != null && (
        <div className="mt-1 text-[10px] text-gray-400">Relevance: {scorePct}%</div>
      )}
    </div>
  );
}

export default function RelevantCardsPanel({
  cards,
  fontSize,
  onAdjustFont,
  onCopyToClipboard,
}) {
  const [newCardId, setNewCardId] = useState(null);

  useEffect(() => {
    if (cards.length === 0) return;
    const latest = cards[cards.length - 1];
    setNewCardId(latest.id);
    const t = setTimeout(() => setNewCardId(null), 6000);
    return () => clearTimeout(t);
  }, [cards.length]);

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-200 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <Layers className="w-4 h-4 text-sky-600" />
          Relevant Cards
          {cards.length > 0 && (
            <span className="text-xs font-normal text-gray-500">({cards.length})</span>
          )}
        </h3>
        <div className="flex items-center gap-1 text-gray-500">
          <button
            type="button"
            onClick={() => onAdjustFont("relevantCards", -1)}
            className="p-1 hover:text-gray-900"
            title="Decrease font size"
          >
            <span className="text-xs font-bold">A-</span>
          </button>
          <button
            type="button"
            onClick={() => onAdjustFont("relevantCards", 1)}
            className="p-1 hover:text-gray-900"
            title="Increase font size"
          >
            <span className="text-xs font-bold">A+</span>
          </button>
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1"
        style={{ fontSize: `${fontSize}px` }}
      >
        {cards.length === 0 ? (
          <div className="text-gray-400 italic py-8 text-center text-xs leading-relaxed">
            Relevant knowledge-base cards appear here automatically as the conversation
            touches topics we have case studies for.
          </div>
        ) : (
          [...cards].reverse().map((c) => (
            <RelevantCard
              key={c.id || c.title}
              card={c}
              onCopy={onCopyToClipboard}
              isNew={c.id === newCardId}
            />
          ))
        )}
      </div>
    </div>
  );
}
